import React, { useEffect } from 'react';
import { useRouter, Navigate } from '@tanstack/react-router';
import { useAuthStore } from '../../stores/authStore';

export const LoginPage: React.FC = () => {
  const router = useRouter();
  const { isAuthenticated, setAuth } = useAuthStore();
  const hasRedirected = React.useRef(false);

  useEffect(() => {
    if (isAuthenticated || hasRedirected.current) {
      return;
    }

    // Check if we have a token in the URL parameters (returning from auth app)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      // Decode JWT to get user info
      try {
        const parts = token.split('.');
        const payload = JSON.parse(atob(parts[1]));
        
        // Create user object from JWT payload
        const user = {
          id: payload.uid,
          email: '', // Not available in JWT
          name: '', // Not available in JWT
          role: 'user',
          createdAt: new Date(),
        };

        // Set authentication state
        setAuth(user, token);
        
        // Redirect to root
        hasRedirected.current = true;
        router.navigate({ to: '/' });
        return;
      } catch (error) {
        console.error('Failed to decode JWT token:', error);
      }
    }

    // If no token, redirect to external login app
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get("returnTo") || "/"; // fallback to dashboard
    const normalizedReturnTo = returnTo.startsWith('/') ? returnTo : `/${returnTo}`;
    if (normalizedReturnTo.startsWith('/login')) {
      hasRedirected.current = true;
      router.navigate({ to: '/' });
      return;
    }

    const loginAppUrl = import.meta.env.VITE_AUTH_APP;
    const forwardAuth = `${window.location.origin}${normalizedReturnTo}`;
    const loginUrl = `${loginAppUrl}?forwardAuth=${encodeURIComponent(forwardAuth)}`;
    
    // Redirect to external login app in same window
    hasRedirected.current = true;
    window.location.href = loginUrl;
  }, [router, setAuth, isAuthenticated]);

  // If already authenticated, redirect to root
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show loading screen while redirecting or processing token
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
        <p className="text-muted-foreground">Redirecting to login...</p>
      </div>
    </div>
  );
};